import React, { useEffect, useState } from "react";
import axios from "axios";
import Banner from "../../Components/Common/Banner";
import courses_banner_bg from "../../Assets/images/course/courses_banner_bg.png";
import { FaArrowRightLong, FaClock, FaUsers, FaStar, FaBookOpen } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import GetInTouchUs from "../../Components/Common/GetInTouchUs";

const CourseCategory = () => {
  const [categoriesData, setCategoriesData] = useState([]);
  const [pageValue, setPageValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const getAllCategories = async () => {
    try {
      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BASE_URL}/courses-category/all-categories`,
        headers: {},
      };

      const response = await axios.request(config);
      setCategoriesData(response.data);
      if (response.data.length > 0) {
        setPageValue(response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch categories");
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  const navigateToDetail = (post) => {
    const titleWithDashes = post.title.replace(/(?!\s&|&\s)\s+/g, "-");

    navigate(`/course/${encodeURIComponent(titleWithDashes)}`, {
      state: { post },
    });
  };

  const renderSkeleton = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      {/* Category tabs skeleton */}
      <div className="flex flex-wrap gap-4 mb-12 justify-center">
        {Array(5)
          .fill("")
          .map((_, index) => (
            <div
              key={index}
              className="h-12 w-32 bg-gray-200 animate-pulse rounded-full"
            ></div>
          ))}
      </div>

      {/* Course cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Array(6).fill("").map((_, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="h-48 bg-gray-200 animate-pulse"></div>
            <div className="p-6 space-y-4">
              <div className="h-6 bg-gray-200 animate-pulse rounded"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 animate-pulse rounded"></div>
                <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4"></div>
              </div>
              <div className="flex justify-between items-center">
                <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Banner
          pageName="Our Courses"
          bgimage={courses_banner_bg}
          breadcrummbs="Home / Courses"
        />
        {renderSkeleton()}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Banner
        pageName="Our Courses"
        bgimage={courses_banner_bg}
        breadcrummbs="Home / Courses"
      />

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Transform Your Skills
                <span className="block text-blue-600">with Our Courses</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Discover expertly crafted courses designed to elevate your professional journey.
                Learn from industry leaders and gain practical skills that matter in today's competitive landscape.
              </p>
              <div className="flex items-center space-x-8 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <FaUsers className="text-blue-500" />
                  <span>10,000+ Students</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaStar className="text-yellow-500" />
                  <span>4.9 Rating</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaBookOpen className="text-green-500" />
                  <span>50+ Courses</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-blue-100 rounded-3xl p-8 transform rotate-3">
                <div className="bg-white rounded-2xl p-6 transform -rotate-3 shadow-xl">
                  <div className="space-y-4">
                    <div className="h-4 bg-blue-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="flex justify-between items-center pt-4">
                      <div className="flex space-x-2">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} className="w-4 h-4 bg-yellow-300 rounded-full"></div>
                        ))}
                      </div>
                      <div className="text-blue-600 font-semibold">$99</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Categories and Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        {/* Advanced Category Tabs */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Course Categories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose from our diverse range of course categories, each designed to meet specific learning objectives
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {categoriesData.map((category, index) => (
              <button
                key={index}
                onClick={() => setPageValue(category)}
                className={`
                  relative px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105
                  ${pageValue === category
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-blue-300'
                  }
                `}
              >
                {category.categoryName}
                {pageValue === category && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-600 rounded-full"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Course Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pageValue?.courses?.map((course, index) => (
            <div
              key={index}
              className={`
                bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2
                ${hoveredCard === index ? 'ring-2 ring-blue-200' : ''}
              `}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Course Image */}
              <div className="relative overflow-hidden h-48 bg-gray-100">
                <img
                  src={course?.courseImage}
                  alt={course?.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute top-4 right-4">
                  <div className="bg-white bg-opacity-90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-700">
                    New
                  </div>
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                  <div className={`
                    bg-white bg-opacity-90 backdrop-blur-sm p-3 rounded-full transition-all duration-300
                    ${hoveredCard === index ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}
                  `}>
                    <FaArrowRightLong className="text-blue-600" />
                  </div>
                </div>
              </div>

              {/* Course Content */}
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900 line-clamp-2 leading-tight">
                    {course?.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                    {course?.description}
                  </p>
                </div>

                {/* Course Meta Info */}
                <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <FaClock className="text-blue-500" />
                    <span>8 weeks</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaUsers className="text-green-500" />
                    <span>245 students</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FaStar className="text-yellow-500" />
                    <span>4.8</span>
                  </div>
                </div>

                {/* Action Button - FIXED */}
                <div className="pt-4">
                  <button
                    onClick={() => navigate(`/course/${course._id}`)}
                    className="w-full bg-gray-900 hover:bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 group"
                  >
                    <span>View Course Details</span>
                    <FaArrowRightLong className="group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {(!pageValue?.courses || pageValue.courses.length === 0) && (
          <div className="text-center py-16">
            <div className="text-gray-400 text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Courses Available</h3>
            <p className="text-gray-600">
              We're working on adding new courses to this category. Check back soon!
            </p>
          </div>
        )}
      </div>

      {/* Get In Touch Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GetInTouchUs />
        </div>
      </div>
    </div>
  );
};

export default CourseCategory;